// Eskom PARCC — domain enumerations, ported verbatim from the original app
// (spec §4.1). Several values contain spaces/colons/ampersands, so they are
// modelled as validated string unions (not GraphQL enums); this module is the
// canonical source of truth, mirrored by Postgres CHECK constraints / reference
// tables in the Data Connect schema.

export const COMPLIANCE_LEVELS = [
  'Pending Review',
  'Compliant',
  'Self-Declared: Non-Compliance: Possible IE',
  'Self-Declared: Non-Compliant: Policy & Procedures',
  'Self-Declared: Non-Compliance Housekeeping',
  'Self-Declared: Not Applicable',
  'Non-Compliance: Possible IE',
  'Non-Compliant: Policy & Procedures',
  'Non-Compliance Housekeeping',
  'Not Applicable',
  'Not Reviewed',
  'Deleted',
  'To delete',
] as const;

/** The levels a reviewer may give ONE FINDING LINE (FindingEditorCard) — the full
 *  list minus the two lifecycle sentinels ('Deleted', 'To delete'). A finding line is
 *  a verdict on a criterion; "delete the transaction" is not a verdict on a line. */
export const SELECTABLE_COMPLIANCE_LEVELS = COMPLIANCE_LEVELS.filter(
  (o) => o !== 'Deleted' && o !== 'To delete',
);

/** The levels a reviewer may set on a TRANSACTION — the working paper's compliance
 *  level, the transaction editor's and the CES sheet's compliance override. Every
 *  verdict PLUS the two lifecycle sentinels: Lindiwe flags transactions for removal
 *  from exactly these surfaces ("the to be deleted or deleted option no longer
 *  appears", 29 Sep 2026). The 13/16 Sep removal was over-reach — the count problem
 *  it guarded against (sentinels inflating Compliant) is handled by the dashboard's
 *  Anomalies bucket, and `calculateTransactionCompliance` now carries an explicit
 *  sentinel through to the effective level so the mark is visible everywhere. */
export const TRANSACTION_COMPLIANCE_LEVELS = COMPLIANCE_LEVELS;

export const NEW_COMPLIANCE_LEVELS = [
  'Possible Non-Compliance',
  'Non-Conformance',
  'Non-Conformance - Administrative Control Weakness',
  'Self-Declared: Possible Non-Compliance',
  'Self-Declared: Non-Conformance',
] as const;

export const ALL_COMPLIANCE_LEVELS = [...COMPLIANCE_LEVELS, ...NEW_COMPLIANCE_LEVELS] as const;
export type ComplianceLevel = (typeof ALL_COMPLIANCE_LEVELS)[number];

// The compliance levels surfaced in the filter dropdowns (feedback/Filters.xlsx)
// — a curated subset of COMPLIANCE_LEVELS, in the owner's listed order. Filtering
// matches against the canonical app spelling stored on transactions.
export const FILTER_COMPLIANCE_OPTIONS = [
  'Compliant',
  'Non-Compliance: Possible IE',
  'Non-Compliant: Policy & Procedures',
  'Non-Compliance Housekeeping',
  'Not Applicable',
  'Pending Review',
] as const;

// CES row status options (spec §4.4) — the empty string is the unset default.
export const CES_STATUS_OPTIONS = [
  '',
  'Compliant',
  'Not-Applicable',
  'Non-Compliant - PFMA',
  'Non-Compliant - Internal Policies, Procedures, Instructions etc.',
  'Non-Compliant - Housekeeping Issue',
] as const;
export type CesStatus = (typeof CES_STATUS_OPTIONS)[number];

// Full anchor vocabulary from the evolved app.
export const ANCHORS = [
  'PFMA',
  'National Treasury',
  'Eskom Policies & Procedures',
  'Legislation',
  'Constitution',
  'Statutory Requirement',
  'Procurement Instruction',
  'Internal Policy',
  'Procedure',
  'Governance',
  'Administrative Control',
] as const;
export type Anchor = (typeof ANCHORS)[number];

// The three anchors surfaced to users (TK framing).
export const PRIMARY_ANCHORS = [
  'PFMA',
  'Eskom Policies & Procedures',
  'Relevant Laws & Regulations',
] as const;

/**
 * LEGACY gap-status vocabulary (pre-alignment-call data). Kept ONLY so the
 * normaliser (`normalizeGapStatus`) and old rows remain readable — new writes
 * and all UI selects use {@link GAP_STATUS_OPTIONS}.
 * @deprecated use GAP_STATUS_OPTIONS
 */
export const GAPS_STATUS_OPTIONS = [
  'Resolved',
  'Partially Resolved',
  'Unresolved',
  'Self-declared',
  'Pending Review',
] as const;
export type GapsStatus = (typeof GAPS_STATUS_OPTIONS)[number];

// Alignment call (9 Jun 2026): gap + finding statuses standardised to a
// four-state lifecycle. "Closed by Self-Declaration" records a buyer who
// acknowledged a finding but could not reach full compliance. An unreviewed
// transaction has NO gap status (blank — not a fifth state).
export const GAP_STATUS_OPTIONS = [
  'Open',
  'In Progress',
  'Closed',
  'Closed by Self-Declaration',
] as const;
export type GapStatus = (typeof GAP_STATUS_OPTIONS)[number];

// Four-state review workflow (owner feedback point 2): Not Reviewed → In
// Progress → Pending Review → Reviewed. 'In Progress' is set when a reviewer
// starts capturing the working paper; existing rows in the three legacy states
// remain valid (additive — no data migration).
export const REVIEW_STATUS_OPTIONS = [
  'Not Reviewed',
  'In Progress',
  'Pending Review',
  'Reviewed',
] as const;
export type ReviewStatus = (typeof REVIEW_STATUS_OPTIONS)[number];

// Role is canonically defined in the access-precedence core (src/lib/access.ts);
// re-exported here so domain consumers keep importing it from the enums barrel.
// ROLES is the runtime list, pinned to Role via `satisfies` so the two cannot drift.
export type { Role } from '../lib/access';
export const ROLES = [
  'admin',
  'manager',
  'teamLead',
  'reviewer',
  'dataCoordinator',
] as const satisfies readonly import('../lib/access').Role[];

// R6 (Lindiwe, 23 Jul 2026): "we would like to update the dataset so that it
// separates POs and LPOs [and] accounts for Panels separately from Contracts."
// Her population file already types the column that way, and the client deck
// (feedback/Audit Recovery Program) has always reported Contracts / Panels /
// Task Orders and Contracts / Purchase Orders / LPOs as SEPARATE rows — the
// merged 'POs / LPOs' bucket was the regression, not the split.
//
// 'Task Orders' is new as a stored value: the old vocabulary had only the two
// qualified variants, so a plain "Task Orders" cell matched nothing and fell
// through to the reference-prefix rule, scattering 49 of her rows across
// Contracts / POs / Other. See inferDataset.
export const DATASET_OPTIONS = [
  'Contracts',
  'Panels',
  'POs',
  'LPOs',
  'Task Orders',
  'Other',
] as const;
export type Dataset = (typeof DATASET_OPTIONS)[number];

/**
 * Pre-R6 stored dataset values. NEVER written, and deliberately not consumed by
 * any runtime code — this is the documented record of what `splitDatasetBuckets.mjs`
 * replaced, pinned by test/domain/enums.test.ts so the two vocabularies can never
 * overlap (an overlap would make a "legacy" value writable again).
 *
 * Un-migrated rows are handled WITHOUT this list: `datasetCategoryFor` resolves
 * them by substring, and `normalizeDatasetFacet` passes 'POs / LPOs' through
 * verbatim on purpose — showing up as a visible outlier in the filter dropdown is
 * how an incomplete migration announces itself, rather than silently landing in
 * the wrong bucket.
 */
export const LEGACY_DATASET_OPTIONS = [
  'POs / LPOs',
  'NTCSA Task Orders',
  'ICT Task Orders',
] as const;
export type LegacyDataset = (typeof LEGACY_DATASET_OPTIONS)[number];

// Division reference set (deduplicated; GX→Gx). CCD added Jun 11 2026 (Usiphile's
// division, surfaced by the team roster). Additive — order is not load-bearing.
// Each entry is a CANONICAL division name (canonicalDivision output): the bare
// 'ICT' was removed Aug 2026 — it folds into 'ICT & Professional Services', and
// leaving both made the register/manage dropdowns list ICT twice (owner feedback).
export const DIVISION_OPTIONS = [
  'PED',
  'GCD',
  'CCD',
  'Tactical Sourcing',
  'Gx',
  'NTCSA',
  'ICT & Professional Services',
] as const;
export type Division = (typeof DIVISION_OPTIONS)[number];

// Raw mechanism variants seen in source data — retained for migration
// normalisation (spec §10). The cleaned set is seeded into a reference table.
export const MECHANISM_OPTIONS = [
  'Formal Tendering - Open Tender',
  'Contract Adjustment',
  'Restricted Tendering - Sole Source',
  'Formal Tendering - Limited Tender Closed',
  'Urgent Procurement',
  'Restricted Tendering - Single Source',
  'Single Source',
  'TBC',
  'Modification',
  'Informal Tendering',
  'Renewable IPP Contracts Checklist Tx',
  'Panel',
  'Formal Tendering',
  'Urgent',
  'Transvesal/Formal',
  'Formal Tendering - Single source',
  'Formal',
  'Renewable IPP Contracts',
  'Closed Tender',
  'Emergency',
  'Transversal',
  'Sole Source',
  'Urgent Procurement PO and Contract',
  'Formal Tendering Contract',
  'Formal tender',
  'Formal - Open Tender',
  'LPO',
  'Emergency Procurement',
  'Restricted Tendering-Sole Source',
  'Condonation',
  'Informal',
  'Standard PO',
  'Formal procurement mechanism',
  'Emergency PO',
  'Informal tender mechanism',
  'Conversion',
  'Local Purchase Order',
  'Task Order',
] as const;
export type Mechanism = (typeof MECHANISM_OPTIONS)[number];

// CES procurement routes (the 8 evaluation paths — spec §4.4).
export const CES_ROUTES = [
  'Formal Tendering - Contracts',
  "Informal Tendering - PO's",
  'LPO',
  'Emergency',
  'Urgent',
  'Sole Source',
  'Single Source',
  'Expansions - Modifications',
] as const;
export type CesRoute = (typeof CES_ROUTES)[number];
